/**
 * useClarity - React Hook for Microsoft Clarity Analytics
 *
 * Provides easy-to-use functions for tracking custom events,
 * identifying users, and managing Clarity analytics.
 *
 * NOTE: This hook does NOT require Router context - it uses window.location
 *
 * Usage:
 *   import { useClarity } from './hooks/useClarity';
 *
 *   function MyComponent() {
 *     const { trackEvent, identifyUser, trackClick } = useClarity();
 *
 *     const handleBooking = () => {
 *       trackEvent('booking_started', { source: 'homepage' });
 *     };
 *   }
 */

import { useCallback, useEffect, useRef } from 'react';

// Check if Clarity is available
const isClarityReady = () => {
    return typeof window !== 'undefined' && typeof window.clarity === 'function';
};

// Check if user has given consent
const hasConsent = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('cookieConsent') === 'true';
};

// Safe way to get current path (no Router needed)
const getCurrentPath = () => {
    if (typeof window === 'undefined') return '';
    return window.location.pathname;
};

export const useClarity = () => {
    const lastPath = useRef(getCurrentPath());

    /**
     * Track page views on route change (using window.location)
     */
    useEffect(() => {
        if (!isClarityReady() || !hasConsent()) return;

        const checkPathChange = () => {
            const currentPath = getCurrentPath();
            if (currentPath !== lastPath.current) {
                lastPath.current = currentPath;
                window.clarity('set', 'page_path', currentPath);
                window.clarity('set', 'page_search', window.location.search);
            }
        };

        // Check on mount
        checkPathChange();

        // Listen for popstate (back/forward navigation)
        window.addEventListener('popstate', checkPathChange);

        return () => {
            window.removeEventListener('popstate', checkPathChange);
        };
    }, []);

    /**
     * Track a custom event
     * @param {string} eventName - Name of the event (e.g., 'button_click', 'form_submit')
     * @param {object} eventData - Optional data to attach to the event
     */
    const trackEvent = useCallback((eventName, eventData = {}) => {
        if (!isClarityReady() || !hasConsent()) {
            console.debug('[Clarity] Not ready or no consent - event not tracked:', eventName);
            return;
        }

        try {
            // Use Clarity's custom tag feature
            window.clarity('set', eventName, JSON.stringify(eventData));

            console.debug('[Clarity] Event tracked:', eventName, eventData);
        } catch (error) {
            console.error('[Clarity] Error tracking event:', error);
        }
    }, []);

    /**
     * Identify a user (for logged-in users)
     * @param {string} userId - Unique user identifier (do NOT use PII like email)
     * @param {string} sessionId - Optional session identifier
     * @param {string} pageName - Optional custom page name
     */
    const identifyUser = useCallback((userId, sessionId = null, pageName = null) => {
        if (!isClarityReady() || !hasConsent()) {
            console.debug('[Clarity] Not ready or no consent - user not identified');
            return;
        }

        try {
            window.clarity('identify', userId, sessionId, pageName);
            console.debug('[Clarity] User identified:', userId);
        } catch (error) {
            console.error('[Clarity] Error identifying user:', error);
        }
    }, []);

    /**
     * Set a custom tag (persists for the session)
     * @param {string} key - Tag key
     * @param {string|number|boolean} value - Tag value
     */
    const setTag = useCallback((key, value) => {
        if (!isClarityReady() || !hasConsent()) {
            return;
        }

        try {
            window.clarity('set', key, String(value));
            console.debug('[Clarity] Tag set:', key, value);
        } catch (error) {
            console.error('[Clarity] Error setting tag:', error);
        }
    }, []);

    /**
     * Track button/link clicks with context
     * @param {string} buttonName - Descriptive name for the button
     * @param {string} location - Where on the page (e.g., 'header', 'hero', 'footer')
     */
    const trackClick = useCallback((buttonName, location = 'unknown') => {
        trackEvent('button_click', {
            button: buttonName,
            location: location,
            page: getCurrentPath(),
            timestamp: new Date().toISOString()
        });
    }, [trackEvent]);

    /**
     * Track form submissions
     * @param {string} formName - Name of the form
     * @param {boolean} success - Whether submission was successful
     * @param {object} metadata - Additional form metadata (don't include PII!)
     */
    const trackFormSubmit = useCallback((formName, success = true, metadata = {}) => {
        trackEvent('form_submit', {
            form: formName,
            success: success,
            page: getCurrentPath(),
            ...metadata
        });
    }, [trackEvent]);

    /**
     * Track CTA (Call to Action) interactions
     * @param {string} ctaName - Name of the CTA
     * @param {string} ctaType - Type: 'button', 'link', 'banner'
     * @param {string} destination - Where the CTA leads to
     */
    const trackCTA = useCallback((ctaName, ctaType = 'button', destination = '') => {
        trackEvent('cta_interaction', {
            cta_name: ctaName,
            cta_type: ctaType,
            destination: destination,
            page: getCurrentPath()
        });
    }, [trackEvent]);

    /**
     * Track video interactions
     * @param {string} videoName - Name/ID of the video
     * @param {string} action - 'play', 'pause', 'complete', 'seek'
     * @param {number} position - Current position in seconds
     */
    const trackVideo = useCallback((videoName, action, position = 0) => {
        trackEvent('video_interaction', {
            video: videoName,
            action: action,
            position: position,
            page: getCurrentPath()
        });
    }, [trackEvent]);

    /**
     * Track scroll depth milestones
     * @param {number} percentage - Scroll depth percentage (25, 50, 75, 100)
     */
    const trackScrollDepth = useCallback((percentage) => {
        trackEvent('scroll_depth', {
            depth: percentage,
            page: getCurrentPath()
        });
    }, [trackEvent]);

    /**
     * Track use case / advent calendar interactions
     * @param {number} day - Day number
     * @param {string} title - Use case title
     * @param {string} action - 'opened', 'clicked_link', 'viewed'
     */
    const trackUseCaseInteraction = useCallback((day, title, action = 'opened') => {
        trackEvent('usecase_interaction', {
            day: day,
            title: title,
            action: action,
            page: getCurrentPath()
        });
    }, [trackEvent]);

    /**
     * Track booking/discovery call clicks
     * @param {string} source - Where the click came from
     */
    const trackBookingClick = useCallback((source = 'unknown') => {
        trackEvent('booking_click', {
            source: source,
            page: getCurrentPath(),
            timestamp: new Date().toISOString()
        });

        // Also track as a CTA
        trackCTA('Book Discovery Call', 'button', 'cal.com');
    }, [trackEvent, trackCTA]);

    /**
     * Upgrade to Clarity Pro session (if using paid tier)
     * Marks current session as important for review
     */
    const upgradeSession = useCallback((reason = 'important') => {
        if (!isClarityReady() || !hasConsent()) {
            return;
        }

        try {
            window.clarity('upgrade', reason);
            console.debug('[Clarity] Session upgraded:', reason);
        } catch (error) {
            console.error('[Clarity] Error upgrading session:', error);
        }
    }, []);

    return {
        // Core functions
        trackEvent,
        identifyUser,
        setTag,

        // Convenience trackers
        trackClick,
        trackFormSubmit,
        trackCTA,
        trackVideo,
        trackScrollDepth,

        // Nebulyx-specific trackers
        trackUseCaseInteraction,
        trackBookingClick,

        // Session management
        upgradeSession,

        // Utility
        isReady: isClarityReady() && hasConsent()
    };
};

/**
 * Hook for automatic scroll depth tracking
 * Does NOT require Router context - uses window.location
 */
export const useScrollDepthTracking = () => {
    const { trackScrollDepth } = useClarity();
    const lastPath = useRef(getCurrentPath());
    const trackedMilestones = useRef(new Set());

    useEffect(() => {
        if (!hasConsent()) return;

        const milestones = [25, 50, 75, 100];

        const handleScroll = () => {
            const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (scrollHeight <= 0) return;

            const scrolled = (window.scrollY / scrollHeight) * 100;

            milestones.forEach(milestone => {
                if (scrolled >= milestone && !trackedMilestones.current.has(milestone)) {
                    trackedMilestones.current.add(milestone);
                    trackScrollDepth(milestone);
                }
            });
        };

        // Reset tracked milestones on path change
        const checkPathChange = () => {
            const currentPath = getCurrentPath();
            if (currentPath !== lastPath.current) {
                lastPath.current = currentPath;
                trackedMilestones.current.clear();
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('popstate', checkPathChange);

        // Also check on click (for SPA navigation)
        const handleClick = () => {
            setTimeout(checkPathChange, 100);
        };
        document.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('popstate', checkPathChange);
            document.removeEventListener('click', handleClick);
        };
    }, [trackScrollDepth]);
};

export default useClarity;